import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, ImageOff, Loader2, Satellite, Camera } from "lucide-react";
import api from "@/lib/api";

const SOURCE_LABEL = { google_solar: "Luftbild (Google)", ki_analysis: "Satellitenbild", upload: "Eigenes Foto" };
const SOURCE_ICON = { google_solar: Satellite, ki_analysis: Satellite, upload: Camera };

// Handyfotos vor dem Upload auf max. 2000 px / JPEG verkleinern.
const compress = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const im = new Image();
  im.onload = () => {
    const k = Math.min(1, 2000 / Math.max(im.naturalWidth, im.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(im.naturalWidth * k);
    c.height = Math.round(im.naturalHeight * k);
    c.getContext("2d").drawImage(im, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("blob"))), "image/jpeg", 0.88);
  };
  im.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
  im.src = url;
});

export function LayoutSourceBar({ projectId, sources, source, onSource, onUploaded, onRemoved, big }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compress(file), "dachfoto.jpg");
      const { data } = await api.post(`/projects/${projectId}/pv-layout/image`, fd);
      toast.success("Dachfoto hochgeladen – Module wurden neu platziert");
      onUploaded(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Foto konnte nicht gelesen werden – bitte JPG oder PNG verwenden.");
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Eigenes Dachfoto entfernen? Die Belegung auf diesem Foto wird verworfen.")) return;
    try {
      const { data } = await api.delete(`/projects/${projectId}/pv-layout/image`);
      toast.success("Dachfoto entfernt");
      onRemoved(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Entfernen fehlgeschlagen"); }
  };
  const keys = Object.keys(sources || {});
  return (
    <div className={`flex flex-wrap items-center gap-2 ${big ? "justify-center" : ""}`} data-testid="roof-layout-source-bar">
      {keys.length > 1 && keys.map((k) => {
        const Icon = SOURCE_ICON[k];
        return (
          <button key={k} type="button" onClick={() => onSource(k)} data-testid={`roof-layout-source-${k}`}
            className={`h-9 px-3 rounded-lg text-[12px] font-bold inline-flex items-center gap-1.5 border transition-colors ${
              source === k ? "bg-sky-500/20 border-sky-400/60 text-sky-100" : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}>
            <Icon size={13} /> {SOURCE_LABEL[k]}
          </button>
        );
      })}
      <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="roof-layout-upload-input" />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid="roof-layout-upload-btn"
        className={`${big ? "h-11 px-5 text-[13px]" : "h-9 px-3 text-[12px]"} rounded-lg bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold inline-flex items-center gap-1.5 disabled:opacity-60`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {sources?.upload ? "Anderes Foto hochladen" : "Eigenes Dachfoto hochladen"}
      </button>
      {sources?.upload && (
        <button type="button" onClick={remove} data-testid="roof-layout-remove-upload"
          className="h-9 px-3 rounded-lg border border-zinc-700 text-zinc-400 hover:text-rose-200 hover:border-rose-500/40 text-[12px] font-semibold inline-flex items-center gap-1.5">
          <ImageOff size={13} /> Foto entfernen
        </button>
      )}
    </div>
  );
}
